import { FC, useCallback, useEffect, useState } from "react";
import { useFormikContext } from "formik";
import { EntityProfileType } from "@common/types/entity";
import { ClientFormSchema } from "../../AddClientSteps/lib";
import { CoaSourceSelect } from "./CoaSourceSelect";
import { CoaPreviewDrawer } from "./CoaPreviewDrawer";
import { useNewClientCoaTemplates } from "./useNewClientCoaTemplates";

interface Props {
    onNext: () => void;
    workspaceId?: string;
}

export const CoaSourceFlow: FC<Props> = ({ onNext, workspaceId }) => {
    const { setFieldValue, values } = useFormikContext<ClientFormSchema>();
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    const { templates, isFetched: areTemplatesFetched } =
        useNewClientCoaTemplates({ workspaceId });

    const isSelectedTemplateMissing =
        areTemplatesFetched &&
        values.coaTemplateId !== null &&
        !templates?.some((item) => item.id === values.coaTemplateId);

    useEffect(() => {
        if (isSelectedTemplateMissing) {
            setFieldValue("coaTemplateId", null);
            setIsPreviewOpen(false);
        }
    }, [isSelectedTemplateMissing, setFieldValue]);

    const backToPicker = useCallback(() => {
        setFieldValue("coaSetupMethod", "kick");
        setFieldValue("coaTemplateId", null);
        setFieldValue(
            "client.legalType",
            EntityProfileType.SOLE_PROPRIETORSHIP,
        );
    }, [setFieldValue]);

    const handleSelectTemplate = useCallback(
        (templateId: string) => {
            setFieldValue("coaTemplateId", templateId);
            const template = templates?.find((item) => item.id === templateId);
            if (template) {
                setFieldValue("client.legalType", template.entityLegalType);
            }
        },
        [setFieldValue, templates],
    );

    const selectedTemplateName = templates?.find(
        (item) => item.id === values.coaTemplateId,
    )?.name;

    return (
        <>
            <CoaSourceSelect
                templates={templates ?? []}
                selectedTemplateId={values.coaTemplateId}
                selectedTemplateName={selectedTemplateName}
                onSelectTemplate={handleSelectTemplate}
                onBack={backToPicker}
                onNext={onNext}
                onPreview={() => setIsPreviewOpen(true)}
            />
            <CoaPreviewDrawer
                open={isPreviewOpen}
                templateId={values.coaTemplateId}
                templateName={selectedTemplateName}
                onClose={() => setIsPreviewOpen(false)}
            />
        </>
    );
};
